import { RequireAccess } from '@/components/require-access'
import {
	AdditionalQuestionsAnalytics,
	CampaignAnalyticsViewModel,
	CampaignFunnelVisualization,
	CampaignNav,
	CandidateScoreStripPlot,
	JobiLoader,
	QuestionDropoffAnalytics,
	WhatsAppCampaignAnalytics,
} from '@/ui'
import {
	CalculatorIcon,
	CalendarIcon,
	ChartBarIcon,
	CurrencyEuroIcon,
	InformationCircleIcon,
	MapPinIcon,
	UserIcon,
} from '@heroicons/react/24/outline'
import { motion } from 'motion/react'

import { useTranslation } from 'react-i18next'
import { getLocale } from '../../../hooks/helpers'

export function CampaignAnalytics({ view, loading, events }: CampaignAnalyticsViewModel) {
	const { t } = useTranslation('analytics')
	const isLoading = loading.analytics
	const isLoadingMetaAnalytics = loading.metaAnalytics
	const analytics = view.analytics
	const metaAnalytics = view.metaAnalytics
	const metaCampaignConcluded = view.metaAnalytics?.metaCampaignConcluded
	const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
	const metaEndDate = view.campaign.metaAdCampaign?.endDate
	const isRecentlyConcluded = metaCampaignConcluded === true
		&& metaEndDate != null
		&& new Date(metaEndDate).getTime() > threeDaysAgo.getTime()
	const candidates = view.candidates
	const isLoadingCandidates = loading.candidates
	const dropoffAnalytics = view.dropoffAnalytics
	const isLoadingDropoffAnalytics = loading.dropoffAnalytics
	const additionalQuestionsAnalytics = view.additionalQuestionsAnalytics
	const isLoadingAdditionalQuestionsAnalytics = loading.additionalQuestionsAnalytics
	const whatsAppAnalytics = view.whatsAppAnalytics
	const isLoadingWhatsAppAnalytics = loading.whatsAppAnalytics

	return (
		<CampaignNav activeCampaignNavLink="analytics">
			<div className="grid grid-rows-[1fr] min-h-[inherit] gap-4 py-4">
				{isLoading
					? (
						<div className="text-sm text-grey-500 flex items-center justify-center h-full">
							<div className="flex items-center flex-col gap-6 max-w-[350px]">
								<JobiLoader />
							</div>
						</div>
					)
					: !analytics
					? (
						<div className="text-sm text-grey-500 flex items-center justify-center h-full">
							<div className="flex items-center flex-col gap-6 max-w-[350px]">
								<div className="scale-200 text-brand-400">
									<ChartBarIcon className="size-6" />
								</div>
								<div className="text-sm text-grey-500 text-center">
									{t('campaignAnalytics.errorLoading')}
								</div>
							</div>
						</div>
					)
					: (
						<div className="px-8 space-y-6">
							<div>
								<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-4">
									{t('campaignAnalytics.keyMetrics')}
								</h2>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
									<MetricCard
										label={t('campaignAnalytics.candidates')}
										value={analytics.candidateCount}
										subtitle={analytics.nonContactableCount > 0
											? t('campaignAnalytics.nonContactable', {
												count: analytics.nonContactableCount,
											})
											: undefined}
										icon={<UserIcon className="size-6" />}
										color="purple"
										delay={0}
									/>
									<RequireAccess permission="campaign:view-budget">
										<MetricCard
											label={t('campaignAnalytics.campaignBudget')}
											value={analytics.budget !== null
												? formatCurrency(analytics.budget / 100)
												: t('campaignAnalytics.notAvailable')}
											subtitle={view.campaign.budgetBreakdown
													&& analytics.budget !== null
													&& view.campaign.budgetBreakdown.metaBudget !== analytics.budget
												? t('campaignAnalytics.toMeta', {
													amount: formatCurrency(
														view.campaign.budgetBreakdown.metaBudget / 100,
													),
												})
												: undefined}
											icon={<CurrencyEuroIcon className="size-6" />}
											color="green"
											delay={0.1}
										/>
										<MetricCard
											label={t('campaignAnalytics.costPerCandidate')}
											value={analytics.costPerCandidate !== null
												? formatCurrency(analytics.costPerCandidate / 100)
												: t('campaignAnalytics.notAvailable')}
											icon={<CalculatorIcon className="size-6" />}
											color="blue"
											delay={0.2}
										/>
									</RequireAccess>
								</div>
							</div>

							{analytics.candidateCount === 0 && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2, delay: 0.3 }}
									className="p-6 bg-amber-50/70 rounded-xl border-2 border-amber-200 shadow-sm"
								>
									<div className="flex gap-4 items-start">
										<div className="text-amber-600 flex-shrink-0 p-2 bg-amber-100 rounded-lg">
											<InformationCircleIcon className="size-6" />
										</div>
										<div className="text-sm text-amber-900">
											<div className="font-semibold text-base mb-2">
												{t('campaignAnalytics.noCandidatesYet')}
											</div>
											<div className="text-amber-800 leading-relaxed">
												{t('campaignAnalytics.noCandidatesDescription')}
											</div>
										</div>
									</div>
								</motion.div>
							)}

							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.2, delay: 0.3 }}
								className="relative overflow-hidden p-6 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 rounded-xl border border-slate-200/60 shadow-sm"
							>
								<div className="relative">
									<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-4">
										{t('campaignAnalytics.campaignDetails')}
									</h2>
									<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
										<InfoRow
											label={t('campaignAnalytics.campaignName')}
											value={view.campaign.title}
										/>
										<InfoRow
											label={t('campaignAnalytics.location')}
											value={view.campaign.location}
											icon={<MapPinIcon className="size-5" />}
										/>
										<InfoRow
											label={t('campaignAnalytics.startDate')}
											value={view.campaign.startedAt
												? formatDate(view.campaign.startedAt)
												: t('campaignAnalytics.notAvailable')}
											icon={<CalendarIcon className="size-5" />}
										/>
										<InfoRow
											label={t('campaignAnalytics.endDate')}
											value={view.campaign.endsAt
												? formatDate(view.campaign.endsAt)
												: t('campaignAnalytics.notAvailable')}
											icon={<CalendarIcon className="size-5" />}
										/>
									</div>
								</div>
							</motion.div>

							{metaCampaignConcluded === false && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2, delay: 0.3 }}
									className="p-6 bg-blue-50/70 rounded-xl border-2 border-blue-200 shadow-sm"
								>
									<div className="flex gap-4 items-start">
										<div className="text-blue-600 flex-shrink-0 p-2 bg-blue-100 rounded-lg">
											<InformationCircleIcon className="size-6" />
										</div>
										<div className="text-sm text-blue-900">
											<div className="font-semibold text-base mb-2">Campaign Still Active</div>
											<div className="text-blue-800 leading-relaxed">
												This campaign is still running. Ad performance data shown below reflects
												results so far and will continue to update until the campaign ends and
												for up to 3 days after it concludes.
											</div>
										</div>
									</div>
								</motion.div>
							)}

							{isRecentlyConcluded && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2, delay: 0.3 }}
									className="p-6 bg-blue-50/70 rounded-xl border-2 border-blue-200 shadow-sm"
								>
									<div className="flex gap-4 items-start">
										<div className="text-blue-600 flex-shrink-0 p-2 bg-blue-100 rounded-lg">
											<InformationCircleIcon className="size-6" />
										</div>
										<div className="text-sm text-blue-900">
											<div className="font-semibold text-base mb-2">Data Not Yet Final</div>
											<div className="text-blue-800 leading-relaxed">
												This campaign concluded recently. Meta ad insights change until 3 days
												after a campaign ends.
											</div>
										</div>
									</div>
								</motion.div>
							)}

							<CampaignFunnelVisualization
								impressions={metaAnalytics?.impressions ?? null}
								reach={metaAnalytics?.reach ?? null}
								clicks={metaAnalytics?.clicks ?? null}
								applications={analytics.submissionCount}
								isLoadingMeta={isLoadingMetaAnalytics}
							/>

							<CandidateScoreStripPlot candidates={candidates} isLoading={isLoadingCandidates} />

							<QuestionDropoffAnalytics
								data={dropoffAnalytics}
								isLoading={isLoadingDropoffAnalytics}
								onDateRangeChange={events?.onDropoffDateRangeChange}
							/>

							<AdditionalQuestionsAnalytics
								data={additionalQuestionsAnalytics}
								isLoading={isLoadingAdditionalQuestionsAnalytics}
								onDateRangeChange={events?.onAdditionalQuestionsDateRangeChange}
							/>

							<WhatsAppCampaignAnalytics
								data={whatsAppAnalytics}
								isLoading={isLoadingWhatsAppAnalytics}
								dateRange={view.whatsAppDateRange}
								onDateRangeChange={events.onWhatsAppDateRangeChange}
							/>
						</div>
					)}
			</div>
		</CampaignNav>
	)
}

function MetricCard({
	label,
	value,
	icon,
	color,
	subtitle,
	delay = 0,
}: {
	label: string
	value: string | number
	icon: React.ReactNode
	color: 'purple' | 'green' | 'blue'
	subtitle?: string
	delay?: number
}) {
	const iconClasses = {
		purple: 'bg-gradient-to-br from-purple-500 to-purple-600 text-white',
		green: 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white',
		blue: 'bg-gradient-to-br from-sky-500 to-sky-600 text-white',
	}

	const valueColorClasses = {
		purple: 'bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent',
		green: 'bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent',
		blue: 'bg-gradient-to-r from-sky-600 to-sky-700 bg-clip-text text-transparent',
	}

	const bgClasses = {
		purple: 'bg-purple-50/50',
		green: 'bg-emerald-50/50',
		blue: 'bg-sky-50/50',
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className={`relative overflow-hidden p-8 rounded-xl border-2 ${
				color === 'purple' ? 'border-purple-200' : color === 'green' ? 'border-emerald-200' : 'border-sky-200'
			} ${bgClasses[color]} shadow-sm hover:shadow-lg transition-all duration-300`}
		>
			<div className="relative">
				<div className="flex items-start justify-between mb-6">
					<div className="text-sm font-semibold text-grey-600 uppercase tracking-wide">{label}</div>
					<div className={`p-3 rounded-xl ${iconClasses[color]} shadow-md`}>
						{icon}
					</div>
				</div>
				<div className={`text-5xl font-bold ${valueColorClasses[color]} mb-2`}>
					{value}
				</div>
				{subtitle && <div className="text-sm text-grey-600 font-medium">{subtitle}</div>}
			</div>
		</motion.div>
	)
}

function MetaMetricCard({
	label,
	value,
	description,
	icon,
	color,
	delay = 0,
}: {
	label: string
	value: string
	description?: string
	icon: React.ReactNode
	color: 'indigo' | 'cyan' | 'orange' | 'teal'
	delay?: number
}) {
	const iconClasses = {
		indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white',
		cyan: 'bg-gradient-to-br from-cyan-500 to-cyan-600 text-white',
		orange: 'bg-gradient-to-br from-orange-500 to-orange-600 text-white',
		teal: 'bg-gradient-to-br from-teal-500 to-teal-600 text-white',
	}

	const valueColorClasses = {
		indigo: 'bg-gradient-to-r from-indigo-600 to-indigo-700 bg-clip-text text-transparent',
		cyan: 'bg-gradient-to-r from-cyan-600 to-cyan-700 bg-clip-text text-transparent',
		orange: 'bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent',
		teal: 'bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent',
	}

	const bgClasses = {
		indigo: 'bg-indigo-50/40',
		cyan: 'bg-cyan-50/40',
		orange: 'bg-orange-50/40',
		teal: 'bg-teal-50/40',
	}

	const borderClasses = {
		indigo: 'border-indigo-200',
		cyan: 'border-cyan-200',
		orange: 'border-orange-200',
		teal: 'border-teal-200',
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className={`relative overflow-hidden p-6 rounded-xl border-2 ${borderClasses[color]} ${
				bgClasses[color]
			} shadow-sm hover:shadow-lg transition-all duration-300`}
		>
			<div className="relative">
				<div className="flex items-start justify-between mb-4">
					<div className="text-sm font-semibold text-grey-600 uppercase tracking-wide">{label}</div>
					<div className={`p-2.5 rounded-xl ${iconClasses[color]} shadow-md`}>
						{icon}
					</div>
				</div>
				<div className={`text-4xl font-bold ${valueColorClasses[color]} mb-2`}>
					{value}
				</div>
				{description && (
					<div className="text-xs text-grey-600 font-medium leading-relaxed">
						{description}
					</div>
				)}
			</div>
		</motion.div>
	)
}

function MetaMetricSkeleton({ delay = 0 }: { delay?: number }) {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className="relative overflow-hidden p-6 rounded-xl border-2 border-slate-200 bg-slate-50/40 shadow-sm"
		>
			<div className="relative">
				<div className="flex items-start justify-between mb-4">
					<div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
					<div className="h-10 w-10 bg-slate-200 rounded-xl animate-pulse" />
				</div>
				<div className="h-10 w-24 bg-slate-200 rounded animate-pulse mb-2" />
				<div className="h-3 w-full bg-slate-200 rounded animate-pulse" />
			</div>
		</motion.div>
	)
}

function InfoRow({
	label,
	value,
	icon,
}: {
	label: string
	value: string
	icon?: React.ReactNode
}) {
	return (
		<div className="flex flex-col p-4 rounded-lg bg-white/40 border border-slate-200/40 hover:bg-white/60 hover:border-slate-300/60 transition-all duration-200">
			<div className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-3">{label}</div>
			<div className="flex items-center gap-2">
				{icon && <div className="text-grey-600 flex-shrink-0">{icon}</div>}
				<div className="text-base font-semibold text-grey-900 truncate">{value}</div>
			</div>
		</div>
	)
}

function formatCurrency(amount: number): string {
	return new Intl.NumberFormat(getLocale(), {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount)
}

function formatDate(date: string | Date): string {
	const dateObj = typeof date === 'string' ? new Date(date) : date
	return new Intl.DateTimeFormat(getLocale(), {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}).format(dateObj)
}

function formatNumber(num: number): string {
	return new Intl.NumberFormat(getLocale()).format(num)
}
